import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function NewWorkspaceDialog({ open, onOpenChange, onCreated, activate = false }) {
  const [form, setForm] = useState({ name: "", sector: "private", description: "", booking_url: "" });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", form);
      if (activate) await api.post(`/workspaces/${data.id}/activate`);
      toast.success("Workspace created - it starts empty. Build an offer to begin.");
      onOpenChange(false);
      setForm({ name: "", sector: "private", description: "", booking_url: "" });
      onCreated?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "workspace_limit") {
        onOpenChange(false);
        toast.error("Workspace limit reached", {
          description: d.message,
          action: { label: "Billing", onClick: () => window.location.assign("/app/admin/billing") },
        });
      } else {
        toast.error("Could not create workspace");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-workspace-dialog">
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>A separate GTM context under the same organization. Starts empty - no demo data is copied.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="ws-name" placeholder="EMEA Outbound" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
              <SelectTrigger className="mt-1.5" data-testid="ws-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private sector</SelectItem>
                <SelectItem value="public">Public sector</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} data-testid="ws-description" placeholder="What this workspace is for" />
          </div>
          <div>
            <Label>Booking link <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input value={form.booking_url} onChange={(e) => setForm({ ...form, booking_url: e.target.value })} className="mt-1.5" data-testid="ws-booking-url" placeholder="https://cal.com/you/30min or Calendly URL" />
            <p className="text-[11px] text-muted-foreground mt-1">Used by the reply inbox "Book" action. You can also connect Calendly/Cal.com later in Integrations.</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="save-ws-btn">
            {busy ? "Creating…" : "Create workspace"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
